import React, { useState, useEffect } from "react";
import { Lock, Loader2, LogOut } from "lucide-react";

import api from "../../services/api";
import { formatMoney } from "../../helpers/money";

/**
 * Full-screen takeover once the API starts answering 402. It only ever calls
 * /billing/*, which the enforcement middleware leaves open on purpose — the
 * customer must always be able to see the debt and settle it.
 */
const SubscriptionBlocked = ({ onLogout }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get("/billing/status")
      .then(({ data }) => setInvoice(data.pendingInvoice))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePay = async () => {
    if (!invoice) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/checkout`);
      window.location.href = data.url;
    } catch {
      setPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-red-500" />
        </div>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Servicio suspendido
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
          El acceso está pausado por una factura pendiente. Al registrarse el pago se
          reactiva de inmediato.
        </p>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : invoice ? (
          <div className="mt-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 text-left space-y-1">
            <p className="text-xs text-slate-400">Factura {invoice.number}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
              {formatMoney(invoice.totalCents, invoice.currency)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Venció el {new Date(invoice.dueAt).toLocaleDateString("es-DO")}
            </p>
          </div>
        ) : (
          <p className="mt-5 text-sm text-slate-500 dark:text-slate-400">
            No pudimos cargar el detalle. Contacta con soporte.
          </p>
        )}

        <div className="mt-6 space-y-2">
          {invoice && (
            <button
              onClick={handlePay}
              disabled={paying}
              className="w-full flex items-center justify-center gap-2 py-3 text-base font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {paying && <Loader2 className="w-5 h-5 animate-spin" />}
              Pagar ahora
            </button>
          )}
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBlocked;
